import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErrorMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogle = () => {
  const redirectUrl = window.location.origin + "/profil";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [kids, setKids] = useState([]);
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const nextUrl = sp.get("next");

  const addKid = () => setKids((k) => (k.length >= 5 ? k : [...k, { name: "", age: "" }]));
  const removeKid = (i) => setKids((k) => k.filter((_, idx) => idx !== i));
  const setKid = (i, field) => (e) => {
    const v = e.target.value;
    setKids((k) => k.map((c, idx) => (idx === i ? { ...c, [field]: v } : c)));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "forgot") {
      setBusy(true);
      try {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("Ha létezik ilyen fiók, elküldtem a jelszó-visszaállító e-mailt. ✿");
        setMode("login");
      } catch (err) { toast.error(apiErrorMsg(err.response?.data?.detail)); }
      finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try {
      const cleanKids = kids
        .filter((c) => c.name && String(c.age).trim() !== "")
        .map((c) => ({ name: c.name, age: Number(c.age) || 0 }));
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password, cleanKids, form.phone);
      toast.success(mode === "login" ? "Sikeres belépés!" : "Sikeres regisztráció!");
      navigate(u.role === "admin" ? "/admin" : (nextUrl || "/profil"));
    } catch (err) {
      toast.error(apiErrorMsg(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="auth-page">
      <div className="p-6"><Link to="/"><Logo className="h-11 w-auto" /></Link></div>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md bg-white rounded-md border border-black/5 shadow-[0_8px_30px_rgb(0,0,0,0.05)] p-8">
          <h1 className="font-serif text-3xl font-light text-ink">
            {mode === "login" ? "Üdv újra itt!" : mode === "forgot" ? "Elfelejtett jelszó" : "Csatlakozz hozzánk"}
          </h1>
          <p className="text-sm text-inkmuted mt-1">
            {mode === "login" ? "Lépj be a foglalásaidhoz." : mode === "forgot" ? "Add meg az e-mail címed, és küldök egy visszaállító linket." : "Regisztrálj, és foglalj helyet eseményeinkre."}
          </p>

          {mode !== "forgot" && (<>
          <button onClick={startGoogle} data-testid="google-login-btn"
            className="mt-6 w-full flex items-center justify-center gap-3 rounded-full border border-ink/15 py-3 text-sm text-ink hover:bg-stone-50 transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
            Folytatás Google-fiókkal
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-inkmuted">
            <span className="flex-1 h-px bg-stone-200" /> vagy <span className="flex-1 h-px bg-stone-200" />
          </div>
          </>)}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input required placeholder="Neved" value={form.name} onChange={set("name")} data-testid="auth-name"
                className="w-full rounded-md border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-poppy/40" />
            )}
            {mode === "register" && (
              <input type="tel" placeholder="Telefonszám (opcionális)" value={form.phone} onChange={set("phone")} data-testid="auth-phone"
                className="w-full rounded-md border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-poppy/40" />
            )}
            {mode === "register" && (
              <div className="rounded-xl border border-stone-200 p-4 space-y-3" data-testid="auth-children">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-ink">Gyermekeim <span className="text-inkmuted font-normal">(opcionális)</span></span>
                  <button type="button" onClick={addKid} disabled={kids.length >= 5} data-testid="auth-add-child"
                    className="text-xs rounded-full border border-poppy/40 text-poppy px-3 py-1.5 hover:bg-poppy/5 transition-colors disabled:opacity-40">+ Gyermek</button>
                </div>
                {kids.length === 0 && <p className="text-xs text-inkmuted">Ha gyermekednek is foglalnál, add hozzá itt (max. 5).</p>}
                {kids.map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`auth-child-row-${i}`}>
                    <input placeholder="Gyermek teljes neve" value={c.name} onChange={setKid(i, "name")} data-testid={`auth-child-name-${i}`}
                      className="flex-1 rounded-md border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-poppy/40" />
                    <input type="number" min="0" max="18" placeholder="Kor" value={c.age} onChange={setKid(i, "age")} data-testid={`auth-child-age-${i}`}
                      className="w-16 rounded-md border border-stone-200 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-poppy/40" />
                    <button type="button" onClick={() => removeKid(i)} data-testid={`auth-child-remove-${i}`} className="text-inkmuted hover:text-red-500 text-lg leading-none px-1">×</button>
                  </div>
                ))}
              </div>
            )}
            <input required type="email" placeholder="E-mail cím" value={form.email} onChange={set("email")} data-testid="auth-email"
              className="w-full rounded-md border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-poppy/40" />
            {mode !== "forgot" && (
              <input required type="password" placeholder="Jelszó" value={form.password} onChange={set("password")} data-testid="auth-password"
                className="w-full rounded-md border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-poppy/40" />
            )}
            {mode === "login" && (
              <div className="text-right -mt-2">
                <button type="button" onClick={() => setMode("forgot")} data-testid="forgot-password-link" className="text-xs text-poppy hover:underline">Elfelejtetted a jelszavad?</button>
              </div>
            )}
            <button type="submit" disabled={busy} data-testid="auth-submit"
              className="w-full rounded-full bg-poppy text-white py-3 text-sm hover:bg-poppy/90 transition-colors disabled:opacity-60">
              {busy ? "Kérlek várj..." : mode === "login" ? "Belépés" : mode === "forgot" ? "Visszaállító link kérése" : "Regisztráció"}
            </button>
          </form>

          {mode === "forgot" ? (
            <p className="mt-6 text-sm text-inkmuted text-center">
              <button onClick={() => setMode("login")} data-testid="auth-switch-mode" className="text-poppy font-medium">Vissza a belépéshez</button>
            </p>
          ) : (
          <p className="mt-6 text-sm text-inkmuted text-center">
            {mode === "login" ? "Még nincs fiókod?" : "Van már fiókod?"}{" "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="auth-switch-mode"
              className="text-poppy font-medium">
              {mode === "login" ? "Regisztrálj" : "Lépj be"}
            </button>
          </p>
          )}
        </div>
      </div>
    </div>
  );
}
